import { api } from "@/api/client";
import { endpoints } from "@/api/endpoints";

/** What the API says about push for the caller. */
export interface PushConfig {
  available: boolean;
  publicKey?: string | null;
  devices: number;
}

/**
 * Whether this browser can show push notifications at all. On an iPhone or iPad that means the app
 * has been added to the home screen first — Safari in a tab has no push.
 */
export function pushSupported(): boolean {
  return typeof window !== "undefined"
    && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

/** This device's current subscription, if it has one. */
export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const registration = await navigator.serviceWorker.getRegistration();
  return (await registration?.pushManager.getSubscription()) ?? null;
}

/** Asks the person, subscribes this device to the hospital's key, and tells the API about it. */
export async function enablePush(publicKey: string): Promise<void> {
  if (!pushSupported()) throw new Error("This browser cannot show notifications.");

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error(permission === "denied"
      ? "Notifications are blocked for this site. Allow them in the browser's site settings, then try again."
      : "Notifications were not allowed.");
  }

  // Registered here as well as at start-up: start-up registration is production-only, and a device
  // can only be subscribed through a service worker.
  await navigator.serviceWorker.register("/sw.js");
  const registration = await navigator.serviceWorker.ready;

  // Always a fresh subscription, so a device subscribed under an earlier key starts working again.
  const existing = await registration.pushManager.getSubscription();
  if (existing) await existing.unsubscribe();

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: fromBase64Url(publicKey),
  });

  await api.post(endpoints.notifications.pushSubscriptions, {
    ...subscription.toJSON(),
    userAgent: navigator.userAgent.slice(0, 300),
  });
}

/** Switches this device off, at the API and in the browser. */
export async function disablePush(): Promise<void> {
  const subscription = await currentSubscription();
  if (!subscription) return;
  await api.post(endpoints.notifications.pushUnsubscribe, { endpoint: subscription.endpoint });
  await subscription.unsubscribe();
}

/** base64url, as VAPID keys are written, to the bytes the Push API wants. */
export function fromBase64Url(value: string): Uint8Array {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
  return Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
}
